import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';

import { EStatus } from '@common/definitions';
import { useEncodedFullPath } from '@common/hooks/url';
import AudienceFiltersPreview from '@common/components/audience-filters-preview';
import Badge from '@common/components/badge/badge';
import { DataTableColumn } from '@common/components/datatable/types';

import { Form } from '@modules/forms/types';
import { FormActions } from '@modules/forms/components/form-actions/form-actions';
import { FormIcon } from '@modules/forms/components/form-icon/form-icon';
import LinkWrapper from '@common/components/linkWrapper';
import Profile from '@common/components/profile';

export default (data: Form[], meta: any, filter: string, columnOptions: any) => {
  const history = useHistory();
  const encodedPath = useEncodedFullPath();
  const [columns, setColumns] = useState<DataTableColumn[]>([]);
  const { networkId } = useParams<{ networkId: string }>();
  const baseUrl = networkId ? `/networks/${networkId}` : '/admin';
  const {
    audiencePreviewData,
    showUpgradeModal,
    canDuplicate,
    handleDuplicateForm,
    handleDeleteForm,
    handleArchiveForm,
    handleUnarchiveForm,
    setTemplateFormId,
  } = columnOptions;

  useEffect(() => {
    setColumns([
      ...(!filter || filter === '' ? [
        {
          key: 'status',
          label: 'Status',
          options: {
            size: 80,
            renderLite: (value: string) => {
              return <Badge status={value as EStatus} />;
            },
          },
        },
      ] : []),
      {
        key: 'title',
        label: 'Name',
        className: 'Form__User',
        options: {
          render: (row: Form) => {
            return (
              <div className="tw-flex tw-flex-col">
                <div className="Table__Cell__Title FormTitle">
                  <FormIcon form={row} />
                  <a
                    onClick={() => {
                      const base = `/admin/forms/${row.id}/edit`;
                      if (row.status === 'live' || row.status === 'archived') {
                        return history.push(`${base}/submissions?back=${encodedPath}`);
                      }
                      return history.push(`${base}/screens?back=${encodedPath}`);
                    }}
                    role="button"
                  >
                    {row.title}
                  </a>
                </div>
                <AudienceFiltersPreview
                  predicates={row?.audience?.predicates}
                  {...audiencePreviewData}
                />
              </div>
            );
          },
        },
      },
      {
        key: 'responded_count',
        label: 'Submission',
        options: {
          size: 120,
          sortable: true,
          align: 'center',
        },
      },
      {
        key: 'created_by',
        label: 'Created By',
        options: {
          size: 200,
          render: (row: Form) => {
            const user = row.created_by;
            if (!user) return null;
            return (
              <LinkWrapper
                url={`${baseUrl}/users/${user.id}/about`}
                className="tw-flex tw-flex-col [&_h5]:!tw-pl-2 tw-text-inherit hover:tw-text-inherit hover:tw-underline"
              >
                <Profile enablePopover size={30} user={user} />
              </LinkWrapper>
            );
          },
        },
      },
      {
        key: 'created_at',
        label: 'Created At',
        options: {
          size: 100,
          renderLite: (value: string) => {
            return moment(value).format('MMM D, YYYY');
          },
        },
      },
      {
        key: 'actions',
        label: '',
        className: 'Table__Action',
        options: {
          size: 20,
          render: (row: Form) => {
            return (
              <FormActions
                item={row}
                canDuplicate={canDuplicate}
                onShowUpgradeModal={showUpgradeModal}
                onArchive={() => handleArchiveForm(row)}
                onSaveAsTemplate={() => setTemplateFormId(row.id)}
                onDuplicate={() => canDuplicate && handleDuplicateForm(row)}
                onDelete={() => handleDeleteForm(row)}
                onUnarchive={() => handleUnarchiveForm(row)}
              />
            );
          },
        },
      },
    ]);
  }, [data, meta, filter, audiencePreviewData]);

  return columns;
};
